<template>
  <div class="waiting-list-modal">
    <CustomModal :show="show"
                 @close="onClose">
      <template #header>
        <h1 class="custom-modal__heading">
          Join waiting list
        </h1>
      </template>

      <div class="waiting-list-modal__body">
        <div v-if="error">
          <h3>Error</h3>

          <p>
            Something has gone wrong whilst trying to process your request. Please try again later.
          </p>

          <p>
            If this issue persists, please contact us.
          </p>
        </div>

        <div v-else-if="instance && webAccountId">
          <template v-if="step < 1">
            <h2 class="waiting-list-modal__subheading">
              You are joining the waiting list for {{ eventTitle }}
            </h2>

            <div class="waiting-list-modal__details">
              <div>
                <span class="waiting-list-modal__item-heading">
                  Date:
                </span>

                {{ instance.startDate }}
              </div>
              <div>
                <span class="waiting-list-modal__item-heading">
                  Location:
                </span>

                {{ instance.location }}
              </div>
              <div>
                <span class="waiting-list-modal__item-heading">
                  Price:
                </span>

                {{ instance.priceExVat }}
              </div>
            </div>
          </template>

          <template v-if="step === 0">
            <div class="waiting-list-modal__num-delegates">
              <b-form-group label="Select number of delegates">
                <b-select v-model="formData.numDelegates"
                          :options="quantityOptions"></b-select>

                <span v-if="!formData.numDelegates && submitAttempted"
                      class="validation-error">
                  Please select a number
                </span>
              </b-form-group>
            </div>

            <b-form-group label="Organisation name">
              <b-input v-model="formData.orgName"
                       placeholder="Organisation name">
              </b-input>

              <span v-if="!formData.orgName && submitAttempted"
                    class="validation-error">
                Please enter your organisation's name
              </span>
            </b-form-group>

            <b-form-group label="Postcode">
              <b-input v-model="formData.postcode"
                       placeholder="Postcode">
              </b-input>

              <span v-if="!formData.postcode && submitAttempted"
                    class="validation-error">
                Please enter your organisation's postcode
              </span>
            </b-form-group>

            <b-form-group label="Contact phone number">
              <b-input v-model="formData.phone"
                       placeholder="Phone number">
              </b-input>

              <span v-if="!formData.phone && submitAttempted"
                    class="validation-error">
                Please enter your phone number
              </span>
            </b-form-group>

            <b-form-group label="Contact email address">
              <b-input v-model="formData.email"
                       type="email"
                       placeholder="Email address">
              </b-input>

              <span v-if="!formData.email && submitAttempted"
                    class="validation-error">
                Please enter your email address
              </span>
            </b-form-group>
          </template>

          <template v-if="step === 1">
            <h2 class="waiting-list-modal__subheading">
              You have successfully joined the waiting list for
              {{ eventTitle }} on {{ instance.startDate }}.
            </h2>

            <p>
              If places become available, we will be in touch. Many thanks.
            </p>
          </template>
        </div>

        <div v-else>
          <p>Please log in to continue.</p>
          <a class="btn btn-accent-6 btn-rounded"
             href="/login">
            Log in
          </a>
        </div>
      </div>

      <template #footer>
        <template v-if="!error && webAccountId">
          <button v-if="step < 1"
                  class="btn btn-accent-6 btn-rounded"
                  @click="onContinue">
            Join waiting list
          </button>

          <button
            v-else-if="step === 1"
            class="btn btn-accent-6 btn-rounded"
            aria-label="Close modal"
            @click="onClose">
            Close
          </button>
        </template>
      </template>
    </CustomModal>
  </div>
</template>

<script>
import CustomModal from "~/Components/CustomModal.vue";
import { joinWaitingList } from "~/Scripts/resources/eventsResource";
import { calcWaitingListAvailability } from "~/Scripts/modules/events";

// #region types
/**
 * @typedef {Object} Address
 * @property {string} addressId UUID
 * @property {string} orgId UUID
 * @property {string} orgName
 * @property {string} line1
 * @property {string} line2
 * @property {string} line3
 * @property {string} town
 * @property {string} region
 * @property {string} postcode
 * @property {number} [countryId]
 * @property {string} country
 */

/**
 * @typedef {Object} Session
 * @property {string} name
 * @property {string} description
 * @property {string} endDateTime Date is already a formatted string
 * @property {string} startDateTime Date is already a formatted string
 * @property {Address} address
 * @property {boolean} limitNumberOfSpaces
 * @property {boolean} [mandatory]
 * @property {number} [maxSpaces]
 * @property {number} [spacesAvailable]
 * @property {number} [spacesBooked]
 */

/**
 * @typedef {Object} EventInstance
 * @property {string} id UUID
 * @property {string} eventId UUID
 * @property {string} [defaultSessionId] UUID
 * @property {number} [productId]
 * @property {boolean} showLocation
 * @property {boolean} showDisabilityOptions
 * @property {boolean} showDietaryRequirements
 * @property {string} location
 * @property {string} duration
 * @property {string} startDate Date is already a formatted string
 * @property {string} endDate Date is already a formatted string
 * @property {string} address
 * @property {string} postcode
 * @property {string} priceExVat
 * @property {number} [vatRate] double
 * @property {boolean} applyVat
 * @property {number} productId
 * @property {number} [spacesAvailable]
 * @property {number} [minDelegates]
 * @property {number} [maxDelegates]
 * @property {number} [maxDelegatesPerOrganisation]
 * @property {number} [maxDelegatesOnWaitingList]
 * @property {Array<Session>} sessions
 * @property {string} instanceType
 */
// #endregion types

export default {
  name: "EventWaitingListModal",
  components: {
    CustomModal
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    /** @type {import('vue').PropType<EventInstance>} */
    instance: {
      type: Object,
      default: () => ( {} )
    },
    eventTitle: {
      type: String,
      default: null
    },
    webAccountId: {
      type: String,
      default: null
    },
    wantedQuantity: {
      type: Number,
      default: null
    }
  },
  data: () => ( {
    step: 0,
    formData: {
      numDelegates: null,
      email: null,
      phone: null,
      postcode: null,
      orgName: null
    },
    submitAttempted: false,
    bookingId: null,
    orderLineId: null,
    error: false
  } ),
  computed: {
    quantityOptions () {
      const result = [];
      if ( this.instance !== null ) {
        const max = calcWaitingListAvailability( this.instance );

        for ( let i = 1; i <= max; i += 1 ) {
          result.push( {
            value: i,
            text: i
          } );
        }
      }

      return result;
    },
    isValid () {
      let result = false;

      if ( this.step === 0 ) {
        result = Object.values( this.formData ).every( x => x );
      } else {
        result = true;
      }

      return result;
    }
  },
  watch: {
    wantedQuantity: {
      immediate: true,
      handler ( val ) {
        if ( !this.formData.numDelegates ) {
          this.formData.numDelegates = val;
        }
      }
    },
    step () {
      this.submitAttempted = false;
    }
  },
  errorCaptured () {
    this.error = true;
  },
  methods: {
    onContinue () {
      this.submitAttempted = true;

      if ( this.step === 0 ) {
        if ( this.isValid ) {
          joinWaitingList( this.instance.id, this.formData )
            .then( ( { data } ) => {
              const { eventBookingId, apiErrorCode = 500 } = data;

              if ( eventBookingId ) {
                this.bookingId = eventBookingId;
                this.step += 1;
              } else {
                throw new Error( apiErrorCode );
              }
            } )
            .catch( this.handleWaitingListAPIError );
        }
      }
    },
    onClose () {
      this.submitAttempted = false;
      Object.keys( this.formData ).forEach( x => {
        this.formData[x] = null;
      } );
      this.$emit( "close" );
    },
    handleWaitingListAPIError ( error ) {
      const { message: errorCode } = error;
      this.$emit( "close" );
      this.$emit( "error", errorCode );
      throw error;
    }
  }
};
</script>

<style lang="less" scoped>
.waiting-list-modal {
  &__subheading {
    font-size: 16px;
    color: #000;
    padding: 15px 0;
  }

  &__details {
    margin-bottom: 15px;
  }

  &__item-heading {
    font-weight: 600;
  }

  &__num-delegates {
    max-width: 200px;
  }

  &__delegates-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__add-another {
    color: #0c499c;
    padding: 0;
    border: 0;
    background: none;
    font-size: 14px;

    &:hover {
      color: darken(#0c499c, 15%);
      text-decoration: underline;
    }
  }
}
</style>
